import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { employeeApi, quickbooksApi, extractErrorMessage } from '../../../services/hrApi';
import type { Employee } from '../../../types';
import './StepStyles.scss';

interface PayrollIntegrationStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
}

const PayrollIntegrationStep: React.FC<PayrollIntegrationStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
}) => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [quickbooksConnected, setQuickbooksConnected] = useState(false);
  const [quickbooksLoading, setQuickbooksLoading] = useState(false);
  const [addingToQuickBooks, setAddingToQuickBooks] = useState(false);
  const [addedToQuickBooks, setAddedToQuickBooks] = useState(false);

  // Fetch employee data
  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
    } else {
      setCheckingStatus(false);
    }
  }, [employeeId]);

  // Check QuickBooks connection status
  const checkQuickBooksStatus = useCallback(async () => {
    setQuickbooksLoading(true);
    try {
      const status = await quickbooksApi.checkStatus();
      setQuickbooksConnected((status.connected && status.valid) || false);
    } catch (error) {
      console.error('Error checking QuickBooks status:', error);
      setQuickbooksConnected(false);
    } finally {
      setQuickbooksLoading(false);
      setCheckingStatus(false);
    }
  }, []);

  // Check QuickBooks status when component loads
  useEffect(() => {
    checkQuickBooksStatus();
  }, [checkQuickBooksStatus]);

  // Listen for QuickBooks auth success message
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'quickbooks-auth-success') {
        checkQuickBooksStatus();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkQuickBooksStatus]);

  // Check if employee is added to QuickBooks
  useEffect(() => {
    if (employee) {
      const isAdded = !!(employee.quickbooks_employee_id && employee.quickbooks_employee_id.trim() !== '');
      setAddedToQuickBooks(isAdded);
      onComplete(isAdded);
    }
  }, [employee, onComplete]);

  const fetchEmployee = async () => {
    if (!employeeId) return;
    try {
      const emp = await employeeApi.getEmployee(employeeId);
      setEmployee(emp);
    } catch (error) {
      console.error('Error fetching employee:', error);
    }
  };

  const handleQuickBooksLogin = async () => {
    try {
      setQuickbooksLoading(true);
      const response = await quickbooksApi.connect();

      if (response.success && response.auth_url) {
        window.open(response.auth_url, '_blank', 'width=600,height=700');
      } else {
        toast.error('Failed to get QuickBooks authorization URL. Please try again.');
      }
    } catch (error) {
      console.error('Error initiating QuickBooks login:', error);
      toast.error('Failed to initiate QuickBooks login. Please try again.');
    } finally {
      setQuickbooksLoading(false);
    }
  };

  const handleAddToQuickBooks = async () => {
    if (!employeeId) {
      toast.error('Employee ID is required');
      return;
    }

    if (!quickbooksConnected) {
      toast.error('Please connect to QuickBooks first');
      return;
    }

    if (!window.confirm('Are you sure you want to add this employee to QuickBooks?')) {
      return;
    }

    setAddingToQuickBooks(true);
    try {
      const result = await quickbooksApi.createEmployee(employeeId);
      
      if (result.success) {
        toast.success('Employee added to QuickBooks successfully!');
        // Refresh employee data to get updated quickbooks_employee_id
        await fetchEmployee();
      } else {
        toast.error(`Failed to add employee to QuickBooks: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error adding employee to QuickBooks:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to add employee to QuickBooks: ${errorMessage}`);
    } finally {
      setAddingToQuickBooks(false);
    }
  };

  // Get status configuration for UI
  const getStatusConfig = (isAdded: boolean) => {
    if (isAdded) {
      return {
        icon: 'bx bx-check-circle',
        iconBackground: '#e8f5e9',
        iconShadow: 'rgba(40, 167, 69, 0.2)',
        borderColor: '#c3e6cb',
        badgeBackground: '#28a745',
        badgeColor: '#ffffff',
        badgeShadow: 'rgba(40, 167, 69, 0.3)',
        badgeIcon: 'bx bx-check',
        gradientColor: '#34ce57',
        description: 'Employee has been successfully added to QuickBooks.',
        statusText: 'Added to QuickBooks'
      };
    } else {
      return {
        icon: 'bx bx-file-blank',
        iconBackground: '#e9ecef',
        iconShadow: 'rgba(108, 117, 125, 0.2)',
        borderColor: '#dee2e6',
        badgeBackground: '#6c757d',
        badgeColor: '#ffffff',
        badgeShadow: 'rgba(108, 117, 125, 0.3)',
        badgeIcon: 'bx bx-edit',
        gradientColor: '#868e96',
        description: 'Employee has not been added to QuickBooks yet.',
        statusText: 'Not Added'
      };
    }
  };

  if (!employeeId) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <h4>Add to QuickBooks</h4>
          <p>Add employee to QuickBooks</p>
        </div>
        <div className="step-info-box">
          <i className="bx bx-info-circle"></i>
          <p>Please complete the previous steps first.</p>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(addedToQuickBooks);

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>Add to QuickBooks</h4>
        <p>Add employee to QuickBooks</p>
        <div className="docusign-status-header">
          {quickbooksLoading ? (
            <span className="docusign-status-loading">
              <i className="bx bx-loader-alt bx-spin"></i>
              Checking QuickBooks...
            </span>
          ) : quickbooksConnected ? (
            <span className="docusign-status-connected">
              <i className="bx bx-check-circle"></i>
              QuickBooks: Logged in
            </span>
          ) : (
            <span className="docusign-status-disconnected">
              <i className="bx bx-x-circle"></i>
              QuickBooks: Not logged in
            </span>
          )}
        </div>
      </div>

      <div className="step-form">
        {/* Loading State */}
        {checkingStatus && (
          <div style={{ 
            marginBottom: '20px', 
            textAlign: 'center', 
            padding: '60px 40px',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(13, 202, 240, 0.3)'
            }}>
              <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '32px', color: '#ffffff' }}></i>
            </div>
            <p style={{ margin: 0, fontSize: '16px', color: '#495057', fontWeight: 500 }}>
              Checking QuickBooks status...
            </p>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#6c757d' }}>
              Please wait while we verify connection status
            </p>
          </div>
        )}

        {/* Status Display */}
        {!checkingStatus && (
          <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
            {/* Big Icon */}
            <div
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '50%',
                background: statusConfig.iconBackground,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                boxShadow: `0 8px 24px ${statusConfig.iconShadow}`
              }}
            >
              <i 
                className={statusConfig.icon} 
                style={{ 
                  fontSize: '48px', 
                  color: statusConfig.badgeBackground
                }}
              ></i>
            </div>
            
            {/* Status Text */}
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '20px',
              fontWeight: 600,
              color: statusConfig.badgeBackground,
              letterSpacing: '0.5px'
            }}>
              {statusConfig.statusText}
            </h3>
            
            {/* Status Message */}
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#6c757d',
              lineHeight: '1.6',
              maxWidth: '400px',
              textAlign: 'center'
            }}>
              {statusConfig.description}
            </p>

            {/* QuickBooks Employee ID (if added) */}
            {addedToQuickBooks && employee?.quickbooks_employee_id && (
              <p style={{
                margin: '16px 0 0 0',
                fontSize: '12px',
                color: '#868e96',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <i className="bx bx-id-card" style={{ fontSize: '14px' }}></i>
                QuickBooks ID: {employee.quickbooks_employee_id}
              </p>
            )}

            {/* Add to QuickBooks Button (if not added) */}
            {!addedToQuickBooks && (
              <div style={{
                marginTop: '32px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  type="button"
                  onClick={handleAddToQuickBooks}
                  disabled={addingToQuickBooks || !quickbooksConnected || quickbooksLoading}
                  style={{
                    padding: '10px 20px',
                    background: quickbooksConnected ? '#28a745' : '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: (addingToQuickBooks || !quickbooksConnected || quickbooksLoading) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    opacity: (addingToQuickBooks || !quickbooksConnected || quickbooksLoading) ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!addingToQuickBooks && quickbooksConnected && !quickbooksLoading) {
                      e.currentTarget.style.background = '#218838';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = quickbooksConnected ? '#28a745' : '#6c757d';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {addingToQuickBooks ? (
                    <>
                      <i className="bx bx-loader-alt bx-spin"></i>
                      Adding...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-plus"></i>
                      Add to QuickBooks
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Overlay when not logged in - covers entire page */}
        {!quickbooksLoading && !quickbooksConnected && (
          <div className="docusign-lock-overlay">
            <div className="docusign-lock-content">
              <i className="bx bx-lock-alt" style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }}></i>
              <h4>Login Required</h4>
              <p>You have to login to QuickBooks in order to add employees</p>
              <button
                type="button"
                className="btn-docusign-login-overlay"
                onClick={handleQuickBooksLogin}
                disabled={quickbooksLoading}
              >
                <i className="bx bx-log-in"></i>
                Login to QuickBooks
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollIntegrationStep;
